import { Outlet, NavLink, useLocation } from 'react-router-dom';
import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard,
  Tags,
  ShoppingCart,
  Settings,
  LogOut,
  Menu,
  X,
  User,
  ChevronDown,
  Leaf,
  TrendingUp,
  Crown,
} from 'lucide-react';
import { useAuthStore, UserVertical } from '@/stores/authStore';
import { clsx } from 'clsx';
import { platformBranding } from '@/config/branding';
import MobileBottomNav from './MobileBottomNav';

// Navigation items with vertical access control
const allNavigation = [
  { 
    name: 'Dashboard', 
    href: '/dashboard', 
    icon: LayoutDashboard, 
    color: 'text-sky-400',
    verticals: ['AGRO', 'INVESTOR'] as UserVertical[], // Everyone sees dashboard
  },
  { 
    name: 'LabelForge', 
    href: '/labels', 
    icon: Tags, 
    color: 'text-green-400',
    verticals: ['AGRO'] as UserVertical[], // Agro
  },
  { 
    name: 'ShopFather', 
    href: '/shop', 
    icon: ShoppingCart, 
    color: 'text-orange-400',
    verticals: ['AGRO'] as UserVertical[], // Agro
  },
  { 
    name: 'Pricing', 
    href: '/pricing', 
    icon: Crown, 
    color: 'text-yellow-400',
    verticals: ['AGRO', 'INVESTOR'] as UserVertical[], // Everyone
  },
];

// Vertical info for display
const verticalInfo: Record<UserVertical, { name: string; icon: any; color: string }> = {
  AGRO: { name: 'Agro Business', icon: Leaf, color: 'from-green-500 to-emerald-600' },
  INVESTOR: { name: 'Investor', icon: TrendingUp, color: 'from-amber-500 to-orange-600' },
};

export default function MainLayout() {
  // Sidebar collapsed by default on mobile, expanded on desktop
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    // Check if we're on desktop (lg breakpoint = 1024px)
    return typeof window !== 'undefined' && window.innerWidth >= 1024;
  });
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const { user, logout } = useAuthStore();
  const location = useLocation();

  // Filter navigation based on user's vertical
  const navigation = useMemo(() => {
    const userVertical = user?.vertical || 'AGRO';
    const userRole = user?.role || 'USER';
    
    // Admin sees everything
    if (userRole === 'ADMIN') {
      return allNavigation;
    }
    
    return allNavigation.filter(item => item.verticals.includes(userVertical));
  }, [user?.vertical, user?.role]);

  const handleLogout = async () => {
    try {
      await fetch('/api/auth/logout', { method: 'POST' });
    } catch {
      // Ignore errors
    }
    logout();
  };

  const currentVertical = user?.vertical ? verticalInfo[user.vertical] : null;

  return (
    <div className="min-h-screen bg-[#F5F1E8]">
      {/* Mobile sidebar backdrop */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/20 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar - Hidden on mobile, shown on desktop */}
      <aside
        className={clsx(
          'fixed top-0 left-0 z-50 h-full bg-white border-r border-gray-200 transition-all duration-300',
          // Mobile: slide in from left when open, hidden when closed
          'max-lg:transform max-lg:transition-transform',
          sidebarOpen ? 'max-lg:translate-x-0' : 'max-lg:-translate-x-full',
          // Desktop: always visible, toggle between expanded/collapsed
          sidebarOpen ? 'lg:w-64' : 'lg:w-20',
          // Mobile always full width when open
          'max-lg:w-72'
        )}
      >
        {/* Logo */}
        <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200">
          <AnimatePresence mode="wait">
            {sidebarOpen ? (
              <motion.div
                key="full"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex flex-col gap-1"
              >
                <img src="/gg-logo-full.svg" alt="Green Gold" className="h-10 w-auto" />
                <p className="text-xs text-slate-500 ml-1">Business Suite</p>
              </motion.div>
            ) : (
              <motion.div
                key="mini"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="mx-auto"
              >
                <img src="/gg-logo.svg" alt="GG" className="w-10 h-10 flex-shrink-0" />
              </motion.div>
            )}
          </AnimatePresence>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 rounded-lg hover:bg-gray-100 text-slate-600 hover:text-slate-900 transition-colors lg:block hidden"
          >
            {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Vertical badge */}
        {currentVertical && sidebarOpen && (
          <div className="mx-4 mt-4 p-3 rounded-lg bg-green-50 border border-green-200">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-[#6B9F54] flex items-center justify-center shadow-sm">
                <currentVertical.icon className="text-white" size={16} />
              </div>
              <div>
                <p className="text-xs text-slate-500">Your vertical</p>
                <p className="text-sm font-medium text-slate-900">{currentVertical.name}</p>
              </div>
            </div>
          </div>
        )}

        {/* Navigation */}
        <nav className="p-4 space-y-2">
          {navigation.map((item) => {
            const isActive = location.pathname === item.href || 
              (item.href !== '/' && location.pathname.startsWith(item.href));

            return (
              <NavLink
                key={item.name}
                to={item.href}
                className={clsx(
                  'flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 border',
                  isActive
                    ? 'bg-green-50 text-[#6B9F54] border-[#6B9F54] font-semibold'
                    : 'text-slate-600 hover:bg-gray-50 hover:text-slate-900 border-gray-200'
                )}
              >
                <item.icon
                  size={16}
                  className={clsx(isActive ? item.color : 'text-current')}
                />
                <AnimatePresence>
                  {sidebarOpen && (
                    <motion.span
                      initial={{ opacity: 0, width: 0 }}
                      animate={{ opacity: 1, width: 'auto' }}
                      exit={{ opacity: 0, width: 0 }}
                      className="whitespace-nowrap overflow-hidden"
                    >
                      {item.name}
                    </motion.span>
                  )}
                </AnimatePresence>
              </NavLink>
            );
          })}
        </nav>

        {/* User section at bottom */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-200">
          <div className="relative">
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className={clsx(
                'w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors border border-gray-200',
                !sidebarOpen && 'justify-center'
              )}
            >
              <div className="w-8 h-8 rounded-full bg-[#6B9F54] flex items-center justify-center shadow-sm">
                <User size={16} className="text-white" />
              </div>
              {sidebarOpen && (
                <>
                  <div className="text-left flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">
                      {user?.name || user?.email}
                    </p>
                    <p className="text-xs text-slate-500 truncate">{user?.role}</p>
                  </div>
                  <ChevronDown
                    size={16}
                    className={clsx(
                      'text-slate-500 transition-transform',
                      userMenuOpen && 'rotate-180'
                    )}
                  />
                </>
              )}
            </button>

            {/* User dropdown */}
            <AnimatePresence>
              {userMenuOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  className="absolute bottom-full left-0 right-0 mb-2 bg-white rounded-xl border border-gray-200 shadow-lg overflow-hidden"
                >
                  <NavLink
                    to="/settings"
                    className="flex items-center gap-3 px-4 py-3 text-slate-700 hover:bg-gray-50 hover:text-slate-900 transition-colors"
                    onClick={() => setUserMenuOpen(false)}
                  >
                    <Settings size={16} />
                    {sidebarOpen && <span>Settings</span>}
                  </NavLink>
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-4 py-3 text-red-600 hover:bg-red-50 hover:text-red-700 transition-colors"
                  >
                    <LogOut size={16} />
                    {sidebarOpen && <span>Logout</span>}
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main
        className={clsx(
          'transition-all duration-300',
          sidebarOpen ? 'lg:pl-64' : 'lg:pl-20'
        )}
      >
        {/* Mobile header */}
        <header className="lg:hidden sticky top-0 z-30 h-16 bg-white/80 backdrop-blur-lg border-b border-gray-200 flex items-center justify-between px-4">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 rounded-lg hover:bg-gray-100 text-slate-600"
          >
            <Menu size={24} />
          </button>
          <div className="absolute left-1/2 transform -translate-x-1/2">
            <img src="/gg-logo-full.svg" alt="Green Gold" className="h-8 w-auto" />
          </div>
          <div className="w-10"></div> {/* Spacer for balance */}
        </header>

        {/* Page content - with bottom padding for mobile nav */}
        <div className="p-4 lg:p-8 min-h-screen pb-24 lg:pb-8">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Outlet />
          </motion.div>
        </div>

        {/* Footer with VT attribution - hidden on mobile to make room for bottom nav */}
        {platformBranding.poweredBy.showInFooter && (
          <footer className="hidden lg:block border-t border-gray-200 py-4">
            <div className="text-center text-xs text-slate-500">
              <a 
                href={platformBranding.poweredBy.url} 
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-slate-700 transition-colors"
              >
                Powered by {platformBranding.poweredBy.name}
              </a>
            </div>
          </footer>
        )}
      </main>

      {/* Mobile Bottom Navigation */}
      <MobileBottomNav />
    </div>
  );
}
